import React from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import {
  Box,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"

import OverviewCard, { OVERVIEW_CHART_CARD_MIN_HEIGHT_PX } from "./OverviewCard"
import { COLORS } from "./constants/colors"
import { formatChartNumber } from "./utils/numberFormat"
import { formatToDDMMYYYY } from "utils/dateUtils"

const headerCellSx = {
  border: "none",
  color: "#94a3b8",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  py: 1.25,
  px: 1.5,
  whiteSpace: "nowrap",
  backgroundColor: "#f8fafc",
}

const bodyCellSx = {
  border: "none",
  color: COLORS.text,
  fontSize: 14,
  fontWeight: 600,
  py: 1.5,
  px: 1.5,
  whiteSpace: "nowrap",
}

const formatAmount = (value, currency) =>
  `${formatChartNumber(value, 2)}${currency ? ` ${currency}` : ""}`

const formatPercent = value => `${formatChartNumber(value, 2)}%`

const ClosedCertificationsTable = ({ rows, isLoading, currency }) => {
  const hasRows = (rows?.length ?? 0) > 0

  return (
    <OverviewCard
      title={i18n.t("overview.invoicingClosedCertificationsTableTitle")}
      infoText={i18n.t("overview.invoicingClosedCertificationsTableTooltip")}
      loading={isLoading}
      sx={{
        minHeight: OVERVIEW_CHART_CARD_MIN_HEIGHT_PX,
        height: "100%",
        maxHeight: "100%",
      }}
      cardContentSx={{ minHeight: 0 }}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Skeleton variant="rectangular" width="100%" height={40} />
          <Skeleton variant="rectangular" width="100%" height={56} />
          <Skeleton variant="rectangular" width="100%" height={56} />
        </Box>
      ) : !hasRows ? (
        <Typography variant="body2" sx={{ color: "#6b7280" }}>
          {i18n.t("overview.invoicingClosedCertificationsTableEmpty")}
        </Typography>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TableContainer
            sx={{
              flex: 1,
              minHeight: 0,
              width: "100%",
              overflow: "auto",
            }}
          >
            <Table size="small" stickyHeader sx={{ minWidth: 520 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  <TableCell sx={{ ...headerCellSx, borderTopLeftRadius: 8 }}>
                    {i18n.t(
                      "overview.invoicingClosedCertificationsTableCertification",
                    )}
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    {i18n.t(
                      "overview.invoicingClosedCertificationsTableCertified",
                    )}
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    {i18n.t(
                      "overview.invoicingClosedCertificationsTableInvoiced",
                    )}
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    {i18n.t(
                      "overview.invoicingClosedCertificationsTablePending",
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...headerCellSx, borderTopRightRadius: 8 }}
                  >
                    {i18n.t(
                      "overview.invoicingClosedCertificationsTablePercent",
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={row.id ?? `${row.revisionDate}-${index}`}
                    sx={{
                      borderBottom:
                        index < rows.length - 1 ? "1px solid #f0f0f0" : "none",
                    }}
                  >
                    <TableCell sx={{ ...bodyCellSx, minWidth: 160 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.75,
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: COLORS.text,
                          }}
                        >
                          {formatToDDMMYYYY(row.revisionDate)}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(
                            100,
                            Math.max(0, row.invoicedPct ?? 0),
                          )}
                          sx={{
                            height: 6,
                            borderRadius: 999,
                            backgroundColor: "#E5E7EB",
                            "& .MuiLinearProgress-bar": {
                              backgroundColor: COLORS.info.color,
                              borderRadius: 999,
                            },
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      {formatAmount(row.certified, currency)}
                    </TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      {formatAmount(row.invoiced, currency)}
                    </TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      {formatAmount(row.pending, currency)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...bodyCellSx, color: COLORS.info.color }}
                    >
                      {formatPercent(row.invoicedPct)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </OverviewCard>
  )
}

ClosedCertificationsTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      revisionDate: PropTypes.string,
      certified: PropTypes.number,
      invoiced: PropTypes.number,
      pending: PropTypes.number,
      invoicedPct: PropTypes.number,
    }),
  ),
  isLoading: PropTypes.bool,
  currency: PropTypes.string,
}

ClosedCertificationsTable.defaultProps = {
  rows: [],
  isLoading: false,
  currency: "",
}

export default ClosedCertificationsTable
